import { PlusCircleOutlined, BugOutlined } from '@ant-design/icons'
import { Horizontal, Vertical, GraviGrid, GraviButton } from '@gravitate-js/excalibrr'

import { type MutableRefObject, useMemo } from 'react'
import type { PointToPointGridProps } from '../types'
import { createPointToPointColumnDefs } from '../utils'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'

import { useRateManagement } from '@api/useFreight/useFreight'

import type { GridApi } from 'ag-grid-community'

/**
 * Component for displaying the grid of point-to-point rates
 */
export function PointToPointGrid({
  pointToPoints,
  onEdit,
  onViewRateHistory,
  gridRef,
  refreshData,
  ratebook,
  onAddPointToPoint,
}: PointToPointGridProps) {
  const { exportPointToPointRates, importPointToPointRates, useCreatePointToPointNew } = useRateManagement()

  // Create column definitions
  const columnDefs = useMemo(() => createPointToPointColumnDefs(), [])

  return (
    <Vertical flex={1}>
      <GraviGrid
        rowData={pointToPoints}
        columnDefs={columnDefs}
        externalRef={gridRef as MutableRefObject<GridApi>}
        agPropOverrides={{
          rowGroupPanelShow: 'always',
          defaultColDef: {
            sortable: true,
            resizable: true,
            enableRowGroup: true,
          },
          getRowId: (params) => params.data?._id || params.data?.id,
          suppressRowClickSelection: false,
          context: { ratebook, onEdit, onViewRateHistory },
        }}
        storageKey='point-to-point'
        controlBarProps={{
          title: 'Point To Point',
          actionButtons: (
            <Horizontal gap={8}>
              <UploadDownloadButtons
                fileName='PointToPoint.xlsx'
                refreshCallback={refreshData}
                downloadEP={() => exportPointToPointRates(ratebook)}
                uploadEP={(formData: FormData) => importPointToPointRates(formData, ratebook)}
                downloadEPData={null}
                normalizeFunction={null}
                asOfDate={null}
                excelOptions={[
                  {
                    title: 'Download P2P Rates',
                    downloadEP: () => exportPointToPointRates(ratebook),
                    fileName: 'PointToPoint.xlsx',
                  },
                  {
                    title: 'Upload P2P Rates',
                    uploadEP: (formData: FormData) => importPointToPointRates(formData, ratebook),
                  },
                ]}
              />
              <GraviButton
                icon={<PlusCircleOutlined />}
                onClick={onAddPointToPoint}
                buttonText='Create Point to Point'
                theme2
              />
            </Horizontal>
          ),
        }}
      />
    </Vertical>
  )
}
